import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createAdminLog, updateUser, updateAdmin } from '../services/firestore';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 2 * 60 * 1000;       // 2-minute warning before logout
const HIDDEN_TIMEOUT_MS = 30 * 60 * 1000; // Auto-logout if tab hidden > 30 min
const EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'input'];
const SESSION_ALIVE_KEY = 'aigi_session_alive';

function SessionTimeoutProvider({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const hiddenAtRef = useRef(null);

  const isLoggedIn = useCallback(() => {
    return localStorage.getItem('UserLoginStatus') === 'true' ||
           localStorage.getItem('UserLoginStatusAdmin') === 'true';
  }, []);

  const logout = useCallback(() => {
    // HIPAA audit log: session timeout
    createAdminLog({
      action: 'session_timeout',
      user_type: localStorage.getItem('UserLoginStatusAdmin') ? 'admin' : 'patient',
    }).catch(() => {});

    // Clear all auth-related localStorage keys
    localStorage.removeItem('UserProfile');
    localStorage.removeItem('UserLoginStatus');
    localStorage.removeItem('UserId');
    localStorage.removeItem('UserProfileAdmin');
    localStorage.removeItem('UserLoginStatusAdmin');
    localStorage.removeItem('UserIdAdmin');
    localStorage.removeItem('AdminRole');
    localStorage.removeItem('savedPassword');
    // HIPAA: Clear session alive flag
    localStorage.removeItem(SESSION_ALIVE_KEY);
    setShowWarning(false);
    window.location.href = '/';
  }, []);

  const resetTimer = useCallback(() => {
    if (!isLoggedIn()) return;
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    setShowWarning(false);

    timeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      warningRef.current = setTimeout(logout, WARNING_MS);
    }, IDLE_TIMEOUT_MS - WARNING_MS);
  }, [isLoggedIn, logout]);

  // HIPAA: Page Visibility API — auto-logout if tab hidden too long
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isLoggedIn()) return;
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else {
        if (hiddenAtRef.current) {
          const elapsed = Date.now() - hiddenAtRef.current;
          hiddenAtRef.current = null;
          if (elapsed >= HIDDEN_TIMEOUT_MS) {
            logout();
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn, logout]);

  // HIPAA: Browser close detection — if last activity was too long ago, force logout.
  // Uses localStorage (persists across tabs) with a timestamp instead of sessionStorage
  // (which is per-tab and causes instant logout when opening new tabs).
  useEffect(() => {
    if (isLoggedIn()) {
      const lastActive = localStorage.getItem(SESSION_ALIVE_KEY);
      if (lastActive) {
        const elapsed = Date.now() - parseInt(lastActive, 10);
        // If browser was closed for longer than the idle timeout, force logout
        if (elapsed > IDLE_TIMEOUT_MS) {
          logout();
          return;
        }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep last-activity timestamp fresh (updates every 60s while logged in)
  useEffect(() => {
    if (!isLoggedIn()) return;
    const updateAlive = () => localStorage.setItem(SESSION_ALIVE_KEY, String(Date.now()));
    updateAlive();
    const interval = setInterval(updateAlive, 60 * 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // Sync browser timezone to Firestore once per session for the logged-in user.
  // This ensures existing users (who logged in before the timezone feature was
  // deployed) get their timezone saved without needing to re-login.
  useEffect(() => {
    if (!isLoggedIn()) return;
    if (sessionStorage.getItem('aigi_tz_synced')) return;

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;

    const patientId = JSON.parse(localStorage.getItem('UserId') || 'null');
    const adminId = JSON.parse(localStorage.getItem('UserIdAdmin') || 'null');

    if (patientId) {
      updateUser(patientId, { timezone: tz }).catch(() => {});
    }
    if (adminId) {
      updateAdmin(adminId, { timezone: tz }).catch(() => {});
    }

    sessionStorage.setItem('aigi_tz_synced', 'true');
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn()) return;
    EVENTS.forEach(e => document.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      EVENTS.forEach(e => document.removeEventListener(e, resetTimer));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [isLoggedIn, resetTimer]);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    resetTimer();
  };

  return (
    <>
      {children}
      {showWarning && (
        <div className="session-timeout-overlay">
          <div className="session-timeout-modal">
            <div className="session-timeout-icon">&#9202;</div>
            <h3>Session Timeout Warning</h3>
            <p>You will be logged out in 2 minutes due to inactivity.</p>
            <div className="session-timeout-actions">
              <button className="session-timeout-btn primary" onClick={handleStayLoggedIn}>
                Stay Logged In
              </button>
              <button className="session-timeout-btn secondary" onClick={logout}>
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SessionTimeoutProvider;
